
import { apiGet, getRequestHeaders } from "@/lib/api-client";
import { isStubResponse } from "@/types/api/customer-detail";
import type {
  CustomerDetailApi,
  CustomerFinancialApi,
  CustomerAgingApi,
  OutstandingInvoicesApiResponse,
  PaymentHistoryApiResponse,
  CommunicationsApiResponse,
  ContactsApiResponse,
  StubResponse,
} from "@/types/api/customer-detail";

/**
 * Get customer details by ID
 */
export async function getCustomerById(customerId: string, signal?: AbortSignal): Promise<CustomerDetailApi> {
  const response = await apiGet<CustomerDetailApi>(`/api/v1/customers/${customerId}`, undefined, signal);
  return response;
}

/**
 * Get customer financial information
 * Returns null if endpoint is not yet implemented
 */
export async function getCustomerFinancial(customerId: string): Promise<CustomerFinancialApi | null> {
  try {
    const response = await apiGet<CustomerFinancialApi | StubResponse>(
      `/api/v1/customers/${customerId}/financial`
    );
    
    if (isStubResponse(response)) {
      console.log("Financial endpoint not yet implemented");
      return null;
    }
    
    return response;
  } catch (error) {
    console.error("Error fetching customer financial data:", error);
    return null;
  }
}

/**
 * Get customer aging information
 * Returns null if endpoint is not yet implemented
 */
export async function getCustomerAging(customerId: string): Promise<CustomerAgingApi | null> {
  try {
    const response = await apiGet<CustomerAgingApi | StubResponse>(
      `/api/v1/customers/${customerId}/aging`
    );
    
    if (isStubResponse(response)) {
      console.log("Aging endpoint not yet implemented");
      return null;
    }
    
    return response;
  } catch (error) {
    console.error("Error fetching customer aging data:", error);
    return null;
  }
}

/**
 * Get customer outstanding invoices
 * Returns empty array if endpoint is not yet implemented
 */
export async function getCustomerOutstandingInvoices(
  customerId: string,
  signal?: AbortSignal
): Promise<OutstandingInvoicesApiResponse> {
  try {
    const response = await apiGet<OutstandingInvoicesApiResponse | StubResponse>(
      `/api/v1/customers/${customerId}/transactions/outstanding`,
      undefined,
      signal
    );
    
    if (isStubResponse(response)) {
      console.log("Outstanding invoices endpoint not yet implemented");
      return {
        invoices: [],
        totalOutstanding: 0,
        totalOverdue: 0,
      };
    }
    
    return response;
  } catch (error) {
    console.error("Error fetching outstanding invoices:", error);
    return {
      invoices: [],
      totalOutstanding: 0,
      totalOverdue: 0,
    };
  }
}

/**
 * Get customer payment history
 * Returns empty array if endpoint is not yet implemented
 */
export async function getCustomerPaymentHistory(
  customerId: string,
  signal?: AbortSignal
): Promise<PaymentHistoryApiResponse> {
  try {
    const response = await apiGet<PaymentHistoryApiResponse | StubResponse>(
      `/api/v1/customers/${customerId}/transactions/payment-history`,
      undefined,
      signal
    );
    
    if (isStubResponse(response)) {
      console.log("Payment history endpoint not yet implemented");
      return {
        payments: [],
        totalPayments: 0,
      };
    }
    
    return response;
  } catch (error) {
    console.error("Error fetching payment history:", error);
    return {
      payments: [],
      totalPayments: 0,
    };
  }
}

/**
 * Get customer communications
 * Returns empty array if endpoint is not yet implemented
 */
export async function getCustomerCommunications(
  customerId: string
): Promise<CommunicationsApiResponse> {
  try {
    const response = await apiGet<CommunicationsApiResponse | StubResponse>(
      `/api/v1/customers/${customerId}/communications`
    );
    
    if (isStubResponse(response)) {
      console.log("Communications endpoint not yet implemented");
      return {
        communications: [],
        totalCount: 0,
      };
    }
    
    return response;
  } catch (error) {
    console.error("Error fetching communications:", error);
    return {
      communications: [],
      totalCount: 0,
    };
  }
}

/**
 * Get customer contacts
 * Returns empty array if endpoint is not yet implemented
 */
export async function getCustomerContacts(customerId: string): Promise<ContactsApiResponse> {
  try {
    const response = await apiGet<ContactsApiResponse | StubResponse>(
      `/api/v1/customers/${customerId}/contacts`
    );
    
    if (isStubResponse(response)) {
      console.log("Contacts endpoint not yet implemented");
      return {
        contacts: [],
        totalCount: 0,
      };
    }
    
    return response;
  } catch (error) {
    console.error("Error fetching contacts:", error);
    return {
      contacts: [],
      totalCount: 0,
    };
  }
}

/**
 * Create a new contact for a customer
 */
export async function createContact(customerId: string, contact: {
  name: string
  email: string
  phone: string
  company?: string
  address?: string
  notes?: string
  isPrimary: boolean
}): Promise<any> {
  const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5236'}/api/v1/customers/${customerId}/contacts`, {
    method: 'POST',
    headers: getRequestHeaders(),
    body: JSON.stringify(contact),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to create contact' }));
    throw new Error(error.message || 'Failed to create contact');
  }

  return response.json();
}

/**
 * Update an existing contact
 */
export async function updateContact(customerId: string, contactId: string, contact: {
  name?: string
  email?: string
  phone?: string
  company?: string
  address?: string
  notes?: string
  isPrimary?: boolean
}): Promise<any> {
  const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5236'}/api/v1/customers/${customerId}/contacts/${contactId}`, {
    method: 'PUT',
    headers: getRequestHeaders(),
    body: JSON.stringify(contact),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to update contact' }));
    throw new Error(error.message || 'Failed to update contact');
  }

  return response.json();
}

/**
 * Delete a contact
 */
export async function deleteContact(customerId: string, contactId: string): Promise<void> {
  const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5236'}/api/v1/customers/${customerId}/contacts/${contactId}`, {
    method: 'DELETE',
    headers: getRequestHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to delete contact' }));
    throw new Error(error.message || 'Failed to delete contact');
  }
}

/**
 * Set a contact as primary
 */
export async function setContactAsPrimary(customerId: string, contactId: string): Promise<any> {
  return updateContact(customerId, contactId, { isPrimary: true });
}
